import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

import bankTransactionService from '@/modules/transaction/api/services'
import { TransactionProcessor, WithdrawStatus, SortOrder } from '@/schema'
import { getWithdrawSoundEnabled } from './useWithdrawSoundPreference'
import { TransactionQueryKeys } from '@/modules/transaction/keys'

import type { GetWithdrawalTransactionsParams } from '@/modules/transaction/types'

const POLLING_INTERVAL = 30000

const pendingWithdrawalsParams: GetWithdrawalTransactionsParams = {
	processor: TransactionProcessor.MANUAL,
	status: [WithdrawStatus.PENDING],
	take: 1,
	skip: 0,
	sortByCreatedAt: SortOrder.desc,
}

export const pendingWithdrawalsQueryOptions = {
	queryKey: [
		TransactionQueryKeys.WITHDRAW_TRANSACTION,
		'pending-withdrawals-poll',
	] as const,
	queryFn: () =>
		bankTransactionService.getWithdrawalTransactions(pendingWithdrawalsParams),
}

function requestNotificationPermission() {
	if ('Notification' in window && Notification.permission === 'default') {
		Notification.requestPermission()
	}
}

function showNotification(count: number) {
	if ('Notification' in window && Notification.permission === 'granted') {
		new Notification('มีรายการถอนรอดำเนินการ', {
			body: `มีรายการถอน ${count} รายการกำลังรอดำเนินการ`,
			icon: '/favicon.ico',
			tag: 'pending-withdrawal',
		})
	}
}

function playSound() {
	const audio = new Audio('/bell.mp3')
	audio.play().catch((error) => {
		console.error('Error playing sound:', error)
	})
}

function alertPendingWithdrawals(count: number) {
	const isSoundEnabled = getWithdrawSoundEnabled()

	if (document.hidden || !isSoundEnabled) {
		showNotification(count)
	} else {
		playSound()
	}
}

export default function usePollingPendingWithdrawals() {
	const { data, dataUpdatedAt } = useQuery({
		...pendingWithdrawalsQueryOptions,
		refetchInterval: POLLING_INTERVAL,
		refetchIntervalInBackground: true,
	})

	// Request notification permission on mount
	useEffect(() => {
		requestNotificationPermission()
	}, [])

	// Alert on every refetch if there are pending withdrawals
	// Using dataUpdatedAt to trigger on every fetch, not just when total changes
	const total = data?.total ?? 0
	useEffect(() => {
		if (total > 0) {
			alertPendingWithdrawals(total)
		}
	}, [dataUpdatedAt, total])
}
